import React from 'react';

import { Box } from '@chakra-ui/react';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useQuery } from 'components/hooks/useQuery';
import { useSidebarNavMatchRoute } from 'components/hooks/useSidebarNavMatchRoute';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import userflow from 'userflow.js';

import { Preferences, QuickHelp, Archive } from 'assets/assetsV1/iconComponents';

import { MODULE_KEYS1 } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { sendClickedOnPreferencesEvent, sendClickedOnQuickHelpEvent } from 'constants/mixpanelHelpers/genericEvents';
import { routes as appRoutes } from 'constants/routes';

import { TextLabel } from 'uiToolkitV1/Typography';

import { NavButton } from './components/NavButton';

export const routeMatchSidebarSharedComps = ({
  routes,
  locationPathname,
  queryParamsToMatch = {},
  queryParams = {},
}) => {
  // eslint-disable-next-line no-restricted-syntax
  for (const route of routes) {
    const match = matchPath(route, {
      path: locationPathname,
      exact: true,
    });

    if (match) {
      // if query params are given to be matched, match those too
      return Object.keys(queryParamsToMatch).reduce(
        (prevVal, queryParamToMatchKey) =>
          prevVal && queryParams[queryParamToMatchKey] === queryParamsToMatch[queryParamToMatchKey],
        true
      );
    }
  }
  return false;
};

export const SideNavContainer = ({ children, ...props }) => (
  <Box h="100%" display="flex" flexDir="column" {...props}>
    {children}
  </Box>
);

export const FixedContainer = ({ children }) => (
  <Box flexGrow={0} px={3}>
    {children}
  </Box>
);

export const ScrollableContainer = ({ children }) => (
  <Box overflowY="auto" px={3} flexGrow={1} flexShrink={1}>
    {children}
  </Box>
);

export const SettingsButton = ({ module, clickHandler }) => {
  const history = useHistory();
  const location = useLocation();
  const queryParams = useQuery();
  return (
    <NavButton
      name="Preferences"
      icon={Preferences}
      selected={Boolean(
        routeMatchSidebarSharedComps({
          routes: [appRoutes.moduleSettings],
          locationPathname: location.pathname,
          queryParamsToMatch: { module },
          queryParams,
        })
      )}
      onClick={() => {
        history.push(`${appRoutes.moduleSettings}?module=${module}`);
        sendClickedOnPreferencesEvent({
          eventProperties: {
            source: module === MODULE_KEYS1.REVIEW ? 'reviews' : module,
          },
        });
        clickHandler();
      }}
    />
  );
};

export const ArchivedGoalsButton = ({ clickHandler }) => {
  const { moduleName } = useOrganisationSettings();
  const history = useHistory();
  const location = useLocation();
  const queryParams = useQuery();

  return (
    <NavButton
      name={`Archived ${modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE)}`}
      icon={Archive}
      selected={Boolean(
        routeMatchSidebarSharedComps({
          routes: [appRoutes.goals.archived],
          locationPathname: location.pathname,
          queryParamsToMatch: {},
          queryParams,
        })
      )}
      onClick={() => {
        history.push(appRoutes.goals.archived);
        clickHandler();
      }}
    />
  );
};

export const QuickHelpButton = () => {
  const { module } = useSidebarNavMatchRoute();

  return (
    <NavButton
      name="Quick Help"
      icon={QuickHelp}
      onClick={() => {
        sendClickedOnQuickHelpEvent({
          eventProperties: {
            source:
              // eslint-disable-next-line no-nested-ternary
              module === MODULE_KEYS1.REVIEW
                ? 'reviews'
                : // eslint-disable-next-line no-nested-ternary
                  module === MODULE_KEYS1.CONTINUOUS_FEEDBACK
                  ? 'feedback'
                  : module === MODULE_KEYS1.ONE_ON_ONE
                    ? '1:1s'
                    : module,
          },
        });
        userflow.toggleResourceCenter();
      }}
    />
  );
};

export const SectionHeading = ({ children, lowercased = false }) => (
  <TextLabel bold textTransform={lowercased ? 'lowercase' : 'uppercase'} px={3} my={2} color="text.g2">
    {children}
  </TextLabel>
);
